import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  TextField,
  Typography,
  Chip,
  IconButton
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import CheckIcon from '@material-ui/icons/Check'
import AttachFileIcon from '@material-ui/icons/AttachFile'
import DeleteIcon from '@material-ui/icons/Delete'
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf'
import { makeStyles } from '@material-ui/core/styles'
import { useSelector } from 'react-redux'
import { Formik, Form, Field } from 'formik'
import { createCommunityPost, updateCommunityPost } from 'store/community'
import { toast } from 'utils/notify'
import { communityPostSchema } from 'schemas/communityPost'
import ProgressButton from 'components/ProgressButton'
import { uploadCommunityPostAttachment, deleteCommunityPostAttachment } from 'utils/index'
import { storageRef } from 'index'

const CreatePostModal = ({ open, onClose, topics = [], isEditing = false, postToEdit = null }) => {
  const classes = useStyles()
  const { conference } = useSelector((state) => state.conference)
  const { user } = useSelector((state) => state.auth)

  const existingAttachment = postToEdit?.attachments?.[0] || null
  const [existingAttachmentUrl, setExistingAttachmentUrl] = useState(null)
  const [removeExisting, setRemoveExisting] = useState(false)

  useEffect(() => {
    if (!open) {
      setExistingAttachmentUrl(null)
      setRemoveExisting(false)
      return
    }

    const loadExistingUrl = async () => {
      if (existingAttachment && existingAttachment.type?.startsWith('image/')) {
        try {
          const relativePath = existingAttachment.path.replace('images/', '')
          const url = await storageRef.child(relativePath).getDownloadURL()
          setExistingAttachmentUrl(url)
        } catch (err) {
          console.error('Failed to load existing attachment URL:', err)
        }
      }
    }
    loadExistingUrl()
  }, [open, existingAttachment])

  const initialValues = {
    title: postToEdit?.title || '',
    content: postToEdit?.content || '',
    topics: postToEdit?.topics?.map((topic) => topic?.id) || [],
    attachment: null
  }

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const postData = {
        title: values.title,
        content: values.content,
        topics: values.topics,
        isCreatedByAdmin: true,
        createdBy: user?.id || 'admin'
      }

      // Handle attachment
      let attachments = []

      if (values.attachment) {
        // New file selected — upload it
        const attachmentMeta = await uploadCommunityPostAttachment(
          values.attachment,
          conference?.id
        )
        attachments = [attachmentMeta]

        // If replacing an existing attachment, delete the old one
        if (isEditing && existingAttachment) {
          await deleteCommunityPostAttachment(existingAttachment.path)
        }
      } else if (isEditing && existingAttachment && !removeExisting) {
        // Keep existing attachment
        attachments = [existingAttachment]
      } else if (isEditing && removeExisting && existingAttachment) {
        // User removed existing attachment
        await deleteCommunityPostAttachment(existingAttachment.path)
        attachments = []
      }

      postData.attachments = attachments

      if (isEditing && postToEdit) {
        await updateCommunityPost(postToEdit?.id, {
          ...postData,
          conferenceId: conference?.id
        })
      } else {
        await createCommunityPost(postData, conference?.id)
        resetForm()
      }
      onClose()
      setSubmitting(false)
    } catch (e) {
      toast.error(`Failed to ${isEditing ? 'update' : 'create'} post, please try again.`)
      setSubmitting(false)
    }
  }

  const handleTopicChange = (topicId, values, setFieldValue) => {
    const currentTopics = [...values.topics]

    if (currentTopics.includes(topicId)) {
      setFieldValue(
        'topics',
        currentTopics.filter((id) => id !== topicId)
      )
    } else {
      setFieldValue('topics', [...currentTopics, topicId])
    }
  }

  return (
    <Dialog open={open} maxWidth="md" fullWidth classes={{ paper: classes.dialog }}>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="h6">{isEditing ? 'Edit Post' : 'Create New Post'}</Typography>
        <IconButton
          size="small"
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Formik
        initialValues={initialValues}
        validationSchema={communityPostSchema}
        onSubmit={handleSubmit}
        enableReinitialize>
        {({ values, errors, touched, handleChange, handleBlur, isSubmitting, setFieldValue }) => (
          <Form>
            <DialogContent>
              <FormControl fullWidth className={classes.formControl}>
                <Field
                  as={TextField}
                  name="title"
                  label="Post Title"
                  variant="outlined"
                  fullWidth
                  error={touched.title && Boolean(errors.title)}
                  helperText={touched.title && errors.title}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.title}
                />
              </FormControl>

              <FormControl fullWidth className={classes.formControl}>
                <Field
                  as={TextField}
                  name="content"
                  label="Content"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={6}
                  error={touched.content && Boolean(errors.content)}
                  helperText={touched.content && errors.content}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.content}
                />
              </FormControl>

              <FormControl component="fieldset" className={classes.formControl}>
                <Typography variant="subtitle1" gutterBottom>
                  Topics
                </Typography>
                <div className={classes.topicsContainer}>
                  {topics.map((topic) => {
                    const isSelected = values.topics.includes(topic?.id)
                    return (
                      <Chip
                        key={topic?.id}
                        label={topic?.title}
                        className={isSelected ? classes.selectedChip : classes.chip}
                        onClick={() => handleTopicChange(topic?.id, values, setFieldValue)}
                        icon={isSelected ? <CheckIcon style={{ color: 'white' }} /> : null}
                        clickable
                      />
                    )
                  })}
                </div>
                {topics.length === 0 && (
                  <Typography variant="body2" color="textSecondary">
                    No topics available
                  </Typography>
                )}
              </FormControl>

              {/* Attachment Section */}
              <FormControl fullWidth className={classes.formControl}>
                <Typography variant="subtitle1" gutterBottom>
                  Attachment (Optional)
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                  Attach one image (JPEG, PNG, GIF, WebP) or PDF file. Max size: 10MB.
                </Typography>

                {/* Existing attachment (when editing and not removed) */}
                {isEditing && existingAttachment && !removeExisting && !values.attachment && (
                  <Box className={classes.attachmentPreview}>
                    {existingAttachment.type?.startsWith('image/') ? (
                      existingAttachmentUrl ? (
                        <img
                          src={existingAttachmentUrl}
                          alt="Existing attachment"
                          className={classes.imagePreview}
                        />
                      ) : (
                        <Box style={{ padding: 16, textAlign: 'center' }}>
                          <CircularProgress size={24} />
                        </Box>
                      )
                    ) : (
                      <Box className={classes.pdfPreview}>
                        <PictureAsPdfIcon color="error" />
                        <Typography variant="body2">{existingAttachment.filename}</Typography>
                      </Box>
                    )}
                    <IconButton
                      size="small"
                      className={classes.removeAttachment}
                      onClick={() => setRemoveExisting(true)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}

                {/* New file preview */}
                {values.attachment && (
                  <Box className={classes.attachmentPreview}>
                    {values.attachment.type?.startsWith('image/') ? (
                      <img
                        src={URL.createObjectURL(values.attachment)}
                        alt="Preview"
                        className={classes.imagePreview}
                      />
                    ) : (
                      <Box className={classes.pdfPreview}>
                        <PictureAsPdfIcon color="error" />
                        <Typography variant="body2">{values.attachment.name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {(values.attachment.size / (1024 * 1024)).toFixed(2)} MB
                        </Typography>
                      </Box>
                    )}
                    <IconButton
                      size="small"
                      className={classes.removeAttachment}
                      onClick={() => setFieldValue('attachment', null)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}

                {/* File input button */}
                {!values.attachment && !(isEditing && existingAttachment && !removeExisting) && (
                  <Box>
                    <input
                      id="attachment-input"
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const file = e.target.files[0]
                        if (file) {
                          setFieldValue('attachment', file)
                        }
                        e.target.value = ''
                      }}
                    />
                    <label htmlFor="attachment-input">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<AttachFileIcon />}
                        size="small">
                        Choose File
                      </Button>
                    </label>
                  </Box>
                )}

                {/* Validation error */}
                {touched.attachment && errors.attachment && (
                  <Typography variant="caption" color="error" style={{ marginTop: 4 }}>
                    {errors.attachment}
                  </Typography>
                )}
              </FormControl>
            </DialogContent>

            <DialogActions className={classes.dialogActions}>
              <Button onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <ProgressButton
                label={isEditing ? 'Update Post' : 'Create Post'}
                inProgress={isSubmitting}
                disabled={isSubmitting}
                type="submit"
              />
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}

const useStyles = makeStyles((theme) => ({
  dialog: {
    borderRadius: theme.spacing(1)
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2, 3)
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500]
  },
  formControl: {
    marginBottom: theme.spacing(3)
  },
  dialogActions: {
    padding: theme.spacing(2, 3)
  },
  topicsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1)
  },
  chip: {
    margin: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: theme.palette.grey[200]
    }
  },
  selectedChip: {
    fontWeight: 600,
    margin: theme.spacing(0.5),
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.main
    }
  },
  attachmentPreview: {
    position: 'relative',
    display: 'inline-block',
    border: '1px solid #e0e0e0',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
  imagePreview: {
    maxWidth: '100%',
    maxHeight: 200,
    borderRadius: theme.spacing(0.5),
    display: 'block'
  },
  pdfPreview: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5)
  },
  removeAttachment: {
    position: 'absolute',
    top: -8,
    right: -8,
    backgroundColor: 'white',
    border: '1px solid #e0e0e0',
    '&:hover': {
      backgroundColor: '#ffebee'
    }
  }
}))

export default CreatePostModal
